import { Spinner } from '@heroui/spinner'
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router'
import { z } from 'zod'

import { userControllerAcceptExistingUserInvitation, userControllerVerifyInvitationV2 } from '@/api/user/user'
import { WorkflowRoute } from '@/app/routes/_app/workflows/workflows'
import { SignInRoute } from '@/app/routes/sign-in'
import { InvitationAcceptPage } from '@/pages/invitation-accept'
import { getAccessToken } from '@/shared/lib/auth'

const searchSchemaValidation = z.object({
  token: z.string().min(1, { message: 'Token is required' }),
})

type InvitationResult = { status: 'accepted' } | { status: 'error' }

export const Route = createFileRoute('/invitation-accept')({
  component: RouteComponent,
  validateSearch: (search) => searchSchemaValidation.parse(search),
  loaderDeps: ({ search: { token } }) => ({ token }),
  loader: async ({ deps: { token } }): Promise<InvitationResult | undefined> => {
    const verifyData = await userControllerVerifyInvitationV2({ token }).catch(() => null)
    if (!verifyData) return { status: 'error' }
    if (!verifyData.isExistingUser) return
    const isLoggedIn = Boolean(getAccessToken())
    if (!isLoggedIn) {
      throw redirect({ to: SignInRoute.to, search: { inviteToken: token } })
    }

    try {
      await userControllerAcceptExistingUserInvitation({ token })
    } catch {
      return { status: 'error' }
    }

    return { status: 'accepted' }
  },
  pendingComponent: () => (
    <div className="flex h-dvh w-screen items-center justify-center">
      <Spinner size="lg" />
    </div>
  ),
  onError: () => {
    throw redirect({ to: WorkflowRoute.to })
  },
})

export const InvitationSignUpRoute = Route

function RouteComponent() {
  const result = Route.useLoaderData()
  const navigate = useNavigate()
  return <InvitationAcceptPage result={result} onAccepted={() => navigate({ to: WorkflowRoute.to })} />
}
